import { hexColors } from '@mobi/settings'

interface ThemeSettings {
  background?: string
  text: {
    color?: string
    fontWeight?:
      | number
      | '-moz-initial'
      | 'inherit'
      | 'initial'
      | 'revert'
      | 'unset'
      | 'bold'
      | 'normal'
      | 'bolder'
      | 'lighter'
      | undefined
  }
  boxShadow?: string
  specialIcon?: string
  borderTop?: string
  borderLeft?: string
  borderRight?: string
  borderBottom?: string
  hasPadding?: boolean
  hasMargin?: boolean
}

export enum ExpandablePanelThemes {
  dark = 'dark',
  light = 'light',
  white = 'white',
  whiteNested = 'whiteNested',
  purple = 'purple',
  greyWhite = 'greyWhite',
  lavender = 'lavender',
  yellow = 'yellow',
}

export interface ExpandablePanelComponentTheme {
  content: ThemeSettings
  header: ThemeSettings
  toggle: ThemeSettings
}

const colors = {
  dark: {
    header: {
      text: hexColors.white,
      background: hexColors.black,
    },
    content: {
      background: hexColors.white,
      text: hexColors.black,
      boxShadow: hexColors.veryLightGrey,
    },
    toggle: {
      text: hexColors.white,
    },
    inline: {
      background: hexColors.whiteSmoke,
      text: hexColors.black,
      boxShadow: hexColors.nightRider,
    },
    informationIcon: hexColors.summerSky,
  },
  light: {
    header: {
      text: hexColors.black,
      background: hexColors.whiteSmoke,
    },
    content: {
      background: hexColors.whiteSmoke,
      text: hexColors.black,
      boxShadow: hexColors.veryLightGrey,
    },
    toggle: {
      text: hexColors.black,
    },
    inline: {
      background: hexColors.whiteSmoke,
      text: hexColors.black,
      boxShadow: hexColors.nightRider,
    },
  },
  white: {
    header: {
      text: hexColors.nero,
      background: hexColors.white,
      border: hexColors.gainsboro,
    },
    content: {
      background: hexColors.white,
      text: hexColors.nero,
      boxShadow: hexColors.veryLightGrey,
    },
    toggle: {
      text: hexColors.darkGrey,
    },
    inline: {
      background: hexColors.white,
      text: hexColors.nero,
      boxShadow: hexColors.nightRider,
    },
  },
  bonusbet: {
    header: {
      text: hexColors.white,
      background: hexColors.summerSky,
    },
    content: {
      background: hexColors.summerSky,
      text: hexColors.white,
      boxShadow: hexColors.veryLightGrey,
    },
    toggle: {
      text: hexColors.white,
    },
    inline: {
      background: hexColors.summerSky,
      text: hexColors.white,
      boxShadow: hexColors.nightRider,
    },
  },
  purple: {
    header: {
      text: hexColors.white,
      background: hexColors.studio,
      specialIcon: hexColors.white,
    },
    content: {
      background: hexColors.white,
      text: hexColors.black,
      boxShadow: hexColors.veryLightGrey,
    },
    toggle: {
      text: hexColors.white,
    },
    inline: {
      background: hexColors.whiteSmoke,
      text: hexColors.black,
      boxShadow: hexColors.nightRider,
    },
  },
  greyWhite: {
    header: {
      text: hexColors.grey,
      background: hexColors.white,
    },
    content: {
      background: hexColors.white,
      text: hexColors.black,
      boxShadow: hexColors.veryLightGrey,
    },
    toggle: {
      text: hexColors.grey,
    },
    inline: {
      background: hexColors.whiteSmoke,
      text: hexColors.black,
      boxShadow: hexColors.nightRider,
    },
  },
}

const darkTheme: ExpandablePanelComponentTheme = {
  content: {
    background: colors.dark.content.background,
    text: { color: colors.dark.content.text },
    boxShadow: colors.dark.content.boxShadow,
  },
  header: {
    background: colors.dark.header.background,
    text: { color: colors.dark.header.text, fontWeight: 'bold' },
    hasPadding: true,
  },
  toggle: { text: { color: colors.dark.toggle.text } },
}

const lightTheme: ExpandablePanelComponentTheme = {
  content: {
    background: colors.light.content.background,
    text: { color: colors.light.content.text },
    boxShadow: colors.light.content.boxShadow,
  },
  header: {
    text: { color: colors.light.header.text, fontWeight: 'bold' },
    background: colors.light.header.background,
    hasPadding: true,
  },
  toggle: { text: { color: colors.light.toggle.text } },
}

const whiteTheme: ExpandablePanelComponentTheme = {
  content: {
    background: colors.white.content.background,
    text: { color: colors.white.content.text },
    boxShadow: colors.white.content.boxShadow,
  },
  header: {
    text: { color: colors.white.header.text, fontWeight: 'bold' },
    background: colors.white.header.background,
    borderBottom: `1px solid ${colors.white.header.border}`,
    borderTop: `1px solid ${colors.white.header.border}`,
    hasPadding: true,
  },
  toggle: { text: { color: colors.white.toggle.text } },
}

const whiteNestedTheme: ExpandablePanelComponentTheme = {
  content: {
    background: colors.white.content.background,
    text: { color: colors.white.content.text },
    boxShadow: colors.white.content.boxShadow,
  },
  header: {
    text: { color: colors.white.header.text, fontWeight: 'bold' },
    background: colors.white.header.background,
    hasMargin: false,
    hasPadding: false,
  },
  toggle: { text: { color: colors.white.toggle.text } },
}

const purpleTheme: ExpandablePanelComponentTheme = {
  content: {
    background: colors.purple.content.background,
    text: { color: colors.purple.content.text },
    boxShadow: colors.purple.content.boxShadow,
  },
  header: {
    text: { color: colors.purple.header.text, fontWeight: 'bold' },
    specialIcon: colors.purple.header.specialIcon,
    background: colors.purple.header.background,
    hasPadding: true,
  },
  toggle: { text: { color: colors.purple.toggle.text } },
}

const greyWhite: ExpandablePanelComponentTheme = {
  content: {
    background: colors.greyWhite.content.background,
    text: { color: colors.greyWhite.content.text },
    boxShadow: colors.greyWhite.content.boxShadow,
  },
  header: {
    text: { color: colors.greyWhite.header.text },
    background: colors.greyWhite.header.background,
    hasMargin: false,
    hasPadding: true,
  },
  toggle: { text: { color: colors.greyWhite.toggle.text } },
}

const lavender: ExpandablePanelComponentTheme = {
  content: {
    text: { color: hexColors.black },
    background: hexColors.lavenderBlue,
  },
  header: {
    text: { color: hexColors.black },
    background: hexColors.lavenderBlue,
  },
  toggle: { text: { color: hexColors.black } },
}

const yellow: ExpandablePanelComponentTheme = {
  content: {
    text: { color: hexColors.black },
    background: hexColors.capeHoney,
  },
  header: {
    text: { color: hexColors.black },
    background: hexColors.capeHoney,
  },
  toggle: { text: { color: hexColors.black } },
}

export function getExpandablePanelTheme(
  theme: ExpandablePanelThemes | undefined
): ExpandablePanelComponentTheme {
  switch (theme) {
    case ExpandablePanelThemes.dark:
      return darkTheme
    case ExpandablePanelThemes.light:
      return lightTheme
    case ExpandablePanelThemes.white:
      return whiteTheme
    case ExpandablePanelThemes.whiteNested:
      return whiteNestedTheme
    case ExpandablePanelThemes.purple:
      return purpleTheme
    case ExpandablePanelThemes.greyWhite:
      return greyWhite
    case ExpandablePanelThemes.lavender:
      return lavender
    case ExpandablePanelThemes.yellow:
      return yellow
    default:
      return darkTheme
  }
}
